/**
 * User Preferences Sync Middleware
 *
 * Persists the user (or household) preferences profile to Firestore on any
 * mutation. Heavily debounced (3s) since preferences accumulate from many
 * small actions (recordRejection / recordAcceptance fire on every meal pick).
 */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  recordRejection,
  recordAcceptance,
  addDislikedProtein,
  removeDislikedProtein,
  addDislikedIngredient,
  removeDislikedIngredient,
  setMaxCookTime,
  setEnableLearning,
  resetPreferences,
  cleanupOldHistory,
  setDayPreference,
  setWeeklyBalance,
  resetMealSchedule,
  setDefaultFamilySize,
  setHasSeenMyTopRatedBanner,
  setDailyDigestOptIn,
  excludeStaple,
  unexcludeStaple,
  setDietaryRestrictions,
  setOtherDietaryNotes,
  setMealFrequencyPreferences,
  setTasteAxes,
  flipTasteAxis,
  setAllergens,
} from './userPreferencesSlice';
import { syncUserPreferencesToCloud } from '../services/userPreferencesSyncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import type { RootState } from './store';

export const userPreferencesSyncMiddleware = createListenerMiddleware();

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 3000;

userPreferencesSyncMiddleware.startListening({
  matcher: isAnyOf(
    recordRejection,
    recordAcceptance,
    addDislikedProtein,
    removeDislikedProtein,
    addDislikedIngredient,
    removeDislikedIngredient,
    setMaxCookTime,
    setEnableLearning,
    resetPreferences,
    cleanupOldHistory,
    setDayPreference,
    setWeeklyBalance,
    resetMealSchedule,
    setDefaultFamilySize,
    setHasSeenMyTopRatedBanner,
    setDailyDigestOptIn,
    excludeStaple,
    unexcludeStaple,
    setDietaryRestrictions,
    setOtherDietaryNotes,
    setMealFrequencyPreferences,
    setTasteAxes,
    flipTasteAxis,
    setAllergens
  ),
  effect: async (_action, listenerApi) => {
    if (!isFirebaseEnabled()) return;
    const state = listenerApi.getState() as RootState;
    const userId = state.auth?.user?.uid;
    if (!userId) return;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      debounceTimer = null;
      const latest = listenerApi.getState() as RootState;
      const householdId = latest.auth?.householdId ?? null;
      try {
        await syncUserPreferencesToCloud(
          userId,
          latest.userPreferences,
          householdId
        );
      } catch (error) {
        console.error('Failed to sync user preferences:', error);
      }
    }, DEBOUNCE_MS);
  },
});

export default userPreferencesSyncMiddleware;
